import { useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import { toast } from 'react-toastify';
import useEmailMarketing from '../../../hooks/useEmailMarketing';
import useEntertainmentOptions from './useEntertainmentOptions';

const BOOKING_CHANNELS = [
  { value: 'webshop', label: 'Webshop' },
  { value: 'callcenter', label: 'Callcenter' },
  { value: 'on-site', label: 'Vor Ort' },
  { value: 'backend', label: 'Backend' },
];

const randomSecret = () => {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  return [...bytes].map(byte => byte.toString(16).padStart(2, '0')).join('');
};

const EntertainmentFilter = ({ block, config, entertainments, readOnly, onConfigChange }) => (
  <Autocomplete
    multiple
    size='small'
    options={entertainments}
    value={entertainments.filter(entertainment =>
      (config.entertainmentIds || []).some(id => String(id) === String(entertainment._id))
    )}
    disabled={readOnly}
    onChange={(event, value) =>
      onConfigChange({ entertainmentIds: value.map(entertainment => entertainment._id) })
    }
    getOptionLabel={option => option?.longName || option?.entertainmentName || ''}
    isOptionEqualToValue={(a, b) => String(a?._id) === String(b?._id)}
    renderInput={params => (
      <TextField {...params} label='Entertainments (leer = alle)' placeholder='Suchen…' />
    )}
  />
);

const ConsentSwitch = ({ config, readOnly, onConfigChange }) => (
  <FormControlLabel
    control={
      <Switch
        checked={config.requireMarketingConsent !== false}
        disabled={readOnly}
        onChange={event => onConfigChange({ requireMarketingConsent: event.target.checked })}
      />
    }
    label='Nur Kontakte mit Marketing-Einwilligung einschreiben'
  />
);

// Settings panel for every trigger (start) block type. The flow builder passes
// the currently loaded flow id so manual segments can enroll contacts inline.
const TriggerSettings = ({ block, flowId, flowStatus, readOnly, onConfigChange }) => {
  const entertainments = useEntertainmentOptions();
  const { enrollContacts } = useEmailMarketing();
  const config = block.config || {};
  const [manualEmails, setManualEmails] = useState('');
  const [enrollBusy, setEnrollBusy] = useState(false);
  const [secretGen, setSecretGen] = useState(0);

  const handleManualEnroll = async () => {
    const emails = manualEmails
      .split(/[\s,;]+/)
      .map(entry => entry.trim())
      .filter(Boolean);
    if (!emails.length) {
      toast.error('Bitte mindestens eine E-Mail-Adresse eingeben.');
      return;
    }
    setEnrollBusy(true);
    try {
      const result = await enrollContacts(flowId, emails);
      // A null/undefined result means the request was stored for re-auth; the
      // auth-escalation modal handles it, so don't show a misleading "0" toast.
      if (!result) return;
      toast.success(
        `${result.enrolled ?? 0} Kontakt(e) eingeschrieben` +
          `${result.notFound ? `, ${result.notFound} unbekannt` : ''}` +
          `${result.skipped ? `, ${result.skipped} übersprungen` : ''}.`
      );
      setManualEmails('');
    } catch (err) {
      if (err?.code === 'MARKETING_FLOW_NOT_ACTIVE') {
        toast.error('Kontakte können nur in aktive Flows eingeschrieben werden.');
      } else {
        toast.error('Fehler beim Einschreiben der Kontakte.');
      }
    } finally {
      setEnrollBusy(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {['bookingCompleted', 'visitCompleted', 'bookingNoShow'].includes(block.type) && (
        <>
          <EntertainmentFilter
            block={block}
            config={config}
            entertainments={entertainments}
            readOnly={readOnly}
            onConfigChange={onConfigChange}
          />
          <Autocomplete
            multiple
            size='small'
            options={BOOKING_CHANNELS}
            value={BOOKING_CHANNELS.filter(channel =>
              (config.channels || []).includes(channel.value)
            )}
            disabled={readOnly}
            onChange={(event, value) =>
              onConfigChange({ channels: value.map(channel => channel.value) })
            }
            getOptionLabel={option => option.label}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            renderInput={params => (
              <TextField {...params} label='Buchungskanäle (leer = alle)' />
            )}
          />
        </>
      )}

      {block.type === 'bookingCompleted' && (
        <>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(config.onlyFirstBooking)}
                disabled={readOnly}
                onChange={event => onConfigChange({ onlyFirstBooking: event.target.checked })}
              />
            }
            label='Nur bei der ersten Buchung des Kunden'
          />
          <TextField
            key={`${block.blockId}-minAmount`}
            size='small'
            type='number'
            label='Mindest-Buchungswert (€, leer = egal)'
            defaultValue={config.minTotalAmount ?? ''}
            disabled={readOnly}
            inputProps={{ min: 0 }}
            onChange={event => {
              const raw = event.target.value;
              onConfigChange({ minTotalAmount: raw === '' ? null : Number(raw) });
            }}
          />
        </>
      )}

      {block.type === 'visitCompleted' && (
        <TextField
          key={`${block.blockId}-hoursAfter`}
          size='small'
          type='number'
          label='Stunden nach Besuchsende'
          defaultValue={Number(config.hoursAfter) || 2}
          disabled={readOnly}
          inputProps={{ min: 0 }}
          onChange={event =>
            onConfigChange({ hoursAfter: Math.max(0, Number(event.target.value) || 0) })
          }
        />
      )}

      {block.type === 'abandonedBooking' && (
        <>
          <EntertainmentFilter
            block={block}
            config={config}
            entertainments={entertainments}
            readOnly={readOnly}
            onConfigChange={onConfigChange}
          />
          <TextField
            key={`${block.blockId}-hoursAfter`}
            size='small'
            type='number'
            label='Stunden nach Abbruch'
            defaultValue={Number(config.hoursAfter) || 3}
            disabled={readOnly}
            inputProps={{ min: 0 }}
            onChange={event =>
              onConfigChange({ hoursAfter: Math.max(0, Number(event.target.value) || 0) })
            }
          />
          <FormControlLabel
            control={
              <Switch
                checked={config.onlyWithConsent !== false}
                disabled={readOnly}
                onChange={event => onConfigChange({ onlyWithConsent: event.target.checked })}
              />
            }
            label='Nur Abbrecher mit angehaktem Marketing-Opt-in'
          />
        </>
      )}

      {block.type === 'inactivity' && (
        <>
          <TextField
            key={`${block.blockId}-inactiveDays`}
            size='small'
            type='number'
            label='Inaktiv seit (Tagen)'
            defaultValue={Number(config.inactiveDays) || 180}
            disabled={readOnly}
            inputProps={{ min: 1 }}
            onChange={event =>
              onConfigChange({ inactiveDays: Math.max(1, Number(event.target.value) || 180) })
            }
          />
          <EntertainmentFilter
            block={block}
            config={config}
            entertainments={entertainments}
            readOnly={readOnly}
            onConfigChange={onConfigChange}
          />
          <Typography variant='caption' color='text.secondary'>
            Tipp: Kombiniere diesen Trigger mit der Wiedereintritts-Einstellung „Cooldown“ in den
            Flow-Einstellungen, damit Kunden nach jeder weiteren Inaktivitätsphase erneut
            angeschrieben werden können.
          </Typography>
        </>
      )}

      {block.type === 'consentGranted' && (
        <>
          <EntertainmentFilter
            block={block}
            config={config}
            entertainments={entertainments}
            readOnly={readOnly}
            onConfigChange={onConfigChange}
          />
          <Typography variant='caption' color='text.secondary'>
            Startet, sobald ein Kontakt eine Marketing-Einwilligung erhält — egal ob über die
            Buchungsstrecke, das Double-Opt-in oder manuell. Der generische Welcome-Trigger ohne
            Listenpflege.
          </Typography>
        </>
      )}

      {block.type === 'tagAdded' && (
        <>
          <TextField
            key={`${block.blockId}-trigger-tags`}
            size='small'
            label='Interne Tags (kommagetrennt)'
            placeholder='z. B. birthday_club'
            defaultValue={(config.tags || []).join(', ')}
            disabled={readOnly}
            onChange={event =>
              onConfigChange({
                tags: event.target.value
                  .split(',')
                  .map(tag => tag.trim())
                  .filter(Boolean),
              })
            }
          />
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {(config.tags || []).map(tag => (
              <Chip key={tag} size='small' label={tag} />
            ))}
          </Box>
          <ConsentSwitch config={config} readOnly={readOnly} onConfigChange={onConfigChange} />
          <Typography variant='caption' color='text.secondary'>
            Startet für Kontakte, die einen dieser internen Tags tragen — etwa birthday_club, das
            eine bestätigte Anmeldung zum Geburtstags-Club setzt. Der Einstieg hängt damit an dem,
            was bei uns über den Kontakt feststeht, statt an einer Listenmitgliedschaft.
          </Typography>
        </>
      )}

      {block.type === 'occasionDue' && (
        <>
          <FormControl fullWidth size='small'>
            <InputLabel id={`fz-occasion-field-${block.blockId}`}>Anlass / Datumsfeld</InputLabel>
            <Select
              labelId={`fz-occasion-field-${block.blockId}`}
              label='Anlass / Datumsfeld'
              value={config.dateField || 'birthdate'}
              disabled={readOnly}
              onChange={event => onConfigChange({ dateField: event.target.value })}
            >
              <MenuItem value='birthdate'>Geburtstag (birthdate)</MenuItem>
            </Select>
          </FormControl>

          <FormControl disabled={readOnly}>
            <FormLabel sx={{ typography: 'body2', mb: 0.5 }}>Wann startet der Flow?</FormLabel>
            <RadioGroup
              value={config.direction || 'on'}
              onChange={event => onConfigChange({ direction: event.target.value })}
            >
              <FormControlLabel
                value='on'
                control={<Radio size='small' />}
                label='am Geburtstag selbst'
              />
              <FormControlLabel
                value='before'
                control={<Radio size='small' />}
                label='X Tage vor dem Geburtstag'
              />
              <FormControlLabel
                value='after'
                control={<Radio size='small' />}
                label='X Tage nach dem Geburtstag'
              />
            </RadioGroup>
          </FormControl>

          {config.direction && config.direction !== 'on' && (
            <TextField
              key={`${block.blockId}-occasion-offset`}
              size='small'
              type='number'
              sx={{ maxWidth: 240 }}
              label={`Tage ${config.direction === 'after' ? 'nach' : 'vor'} dem Geburtstag (X)`}
              defaultValue={Number(config.offsetDays) || 0}
              disabled={readOnly}
              inputProps={{ min: 0 }}
              onChange={event => {
                const parsed = parseInt(event.target.value, 10);
                onConfigChange({ offsetDays: Number.isNaN(parsed) ? 0 : Math.max(0, parsed) });
              }}
            />
          )}

          <TextField
            key={`${block.blockId}-occasion-tags`}
            size='small'
            label='Nur Kontakte mit diesen internen Tags (kommagetrennt)'
            placeholder='z. B. birthday_club'
            defaultValue={(config.requiredTags || []).join(', ')}
            disabled={readOnly}
            onChange={event =>
              onConfigChange({
                requiredTags: event.target.value
                  .split(',')
                  .map(tag => tag.trim())
                  .filter(Boolean),
              })
            }
          />
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {(config.requiredTags || []).map(tag => (
              <Chip key={tag} size='small' label={tag} />
            ))}
          </Box>

          <ConsentSwitch config={config} readOnly={readOnly} onConfigChange={onConfigChange} />

          <Typography variant='caption' color='text.secondary'>
            Der Anlass selbst ist der Einstieg: Jeden Tag werden die Kontakte eingeschrieben, deren
            Datum heute erreicht ist — jedes Jahr aufs Neue, ohne dass jemand dazwischen eine
            offene Einschreibung mit sich herumträgt. Die Tags bestimmen, für wen das gilt: Ohne
            birthday_club läuft der Flow für jeden Kontakt mit hinterlegtem Geburtstag, nicht nur
            für die Angemeldeten.
          </Typography>
        </>
      )}

      {['bookingCompleted', 'visitCompleted', 'bookingNoShow', 'inactivity'].includes(
        block.type
      ) && <ConsentSwitch config={config} readOnly={readOnly} onConfigChange={onConfigChange} />}

      {block.type === 'manualSegment' && (
        <>
          <Typography variant='body2' color='text.secondary'>
            Kontakte werden ausschließlich manuell eingeschrieben — ideal für einmalige Kampagnen
            an eine handverlesene Zielgruppe.
          </Typography>
          <TextField
            size='small'
            label='E-Mail-Adressen (durch Komma/Zeilenumbruch getrennt)'
            multiline
            minRows={3}
            value={manualEmails}
            onChange={event => setManualEmails(event.target.value)}
            disabled={readOnly}
          />
          <Button
            variant='outlined'
            size='small'
            startIcon={<GroupAddIcon />}
            disabled={readOnly || enrollBusy || flowStatus !== 'active'}
            onClick={handleManualEnroll}
          >
            Kontakte jetzt einschreiben
          </Button>
          {flowStatus !== 'active' && (
            <Typography variant='caption' color='text.secondary'>
              Der Flow muss aktiv sein, um Kontakte einzuschreiben.
            </Typography>
          )}
        </>
      )}

      {block.type === 'apiTrigger' && (
        <>
          <TextField
            // Key is bumped only on programmatic "generate", never on keystroke,
            // so typing does not remount the field and steal focus.
            key={`${block.blockId}-secret-${secretGen}`}
            size='small'
            label='Trigger-Secret (min. 16 Zeichen)'
            defaultValue={config.triggerSecret || ''}
            disabled={readOnly}
            onBlur={event => onConfigChange({ triggerSecret: event.target.value.trim() })}
          />
          <Button
            variant='outlined'
            size='small'
            startIcon={<AutorenewIcon />}
            disabled={readOnly}
            onClick={() => {
              onConfigChange({ triggerSecret: randomSecret() });
              setSecretGen(previous => previous + 1);
            }}
          >
            Neues Secret generieren
          </Button>
          <Typography variant='caption' color='text.secondary' sx={{ wordBreak: 'break-all' }}>
            Externe Systeme lösen den Flow aus über:{' '}
            <code>
              POST {'{Backend}'}/api/v1/marketing-public/trigger{' '}
              {`{ "flowId": "${flowId || '<flowId>'}", "secret": "<Secret>", "email": "kunde@example.com" }`}
            </code>{' '}
            — eingeschrieben werden nur bestehende Kontakte mit Marketing-Einwilligung.
          </Typography>
        </>
      )}
    </Box>
  );
};

export default TriggerSettings;
